import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchAssociations,
  getAllAssociations,
  getAssociationById,
  createAssociation,
  updateAssociation,
  deleteAssociation,
  toggleAssociationActive,
} from "../../../../services/associationsServices";

import { ASSOCIATIONS_INITIAL_FORM } from "../data/associationsAdminData";

import {
  buildAssociationsSearchParams,
  getAssociationsStats,
  normalizeAssociationForm,
  validateAssociationForm,
} from "../utils/associationsAdminUtils";

export default function useAssociationsAdmin() {
  const mountedRef = useRef(true);

  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [hasLogo, setHasLogo] = useState("all");
  const [withGps, setWithGps] = useState("all");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState(ASSOCIATIONS_INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});

  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit), 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getAssociationsStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    window.clearTimeout(showNotice.timeout);
    showNotice.timeout = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildAssociationsSearchParams({
        q,
        categorie,
        stateFilter,
        hasLogo,
        withGps,
        page,
        limit,
      });

      const res = await searchAssociations(params);

      const hasNoAdminResult =
        !res?.items?.length &&
        !q &&
        !categorie &&
        stateFilter === "all" &&
        hasLogo === "all" &&
        withGps === "all" &&
        page === 1;

      if (hasNoAdminResult) {
        const pub = await getAllAssociations();
        const fallbackItems = Array.isArray(pub) ? pub : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        return;
      }

      if (!mountedRef.current) return;

      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (fetchError) {
      console.error("Erreur chargement associations :", fetchError);

      try {
        const pub = await getAllAssociations();
        const fallbackItems = Array.isArray(pub) ? pub : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        setError(
          "Le chargement administrateur a échoué. Affichage d’une liste de secours.",
        );
      } catch (fallbackError) {
        console.error("Erreur fallback associations :", fallbackError);

        if (!mountedRef.current) return;

        setItems([]);
        setTotal(0);
        setError("Impossible de charger les associations pour le moment.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [q, categorie, stateFilter, hasLogo, withGps, page, limit]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeCategorie = useCallback((value) => {
    setCategorie(value);
    setPage(1);
  }, []);

  const changeStateFilter = useCallback((value) => {
    setStateFilter(value);
    setPage(1);
  }, []);

  const changeHasLogo = useCallback((value) => {
    setHasLogo(value);
    setPage(1);
  }, []);

  const changeWithGps = useCallback((value) => {
    setWithGps(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 24);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setCategorie("");
    setStateFilter("all");
    setHasLogo("all");
    setWithGps("all");
    setLimit(24);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm(ASSOCIATIONS_INITIAL_FORM);
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getAssociationById(id);

      if (!row) {
        setError("Association introuvable.");
        return;
      }

      setCurrentId(id);
      setForm({
        nom_asso: row.nom_asso || "",
        categorie: row.categorie || "",
        description_asso: row.description_asso || "",
        contact_asso: row.contact_asso || "",
        fonction_asso: row.fonction_asso || "",
        tel_asso: row.tel_asso || "",
        mail_asso: row.mail_asso || "",
        website_asso: row.website_asso || "",
        facebook_asso: row.facebook_asso || "",
        logo_asso: row.logo_asso || "",
        adresse_asso: row.adresse_asso || "",
        gps_asso: row.gps_asso || "",
        is_active: !!row.is_active,
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture association :", editError);
      setError("Impossible d’ouvrir cette association.");
    } finally {
      setSaving(false);
    }
  }, []);

  const openEditFromPreview = useCallback(async () => {
    if (!previewItem?.id) return;

    const id = previewItem.id;
    setPreviewItem(null);
    await openEdit(id);
  }, [openEdit, previewItem]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm(ASSOCIATIONS_INITIAL_FORM);
    setFieldErrors({});
    setMediaPickerOpen(false);
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateAssociationForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setError("");

      const payload = normalizeAssociationForm(form);

      if (currentId) {
        await updateAssociation(currentId, payload);
        showNotice("Association modifiée avec succès.");
      } else {
        await createAssociation(payload);
        showNotice("Association créée avec succès.");
      }

      closeForm();
      await fetchData();
    } catch (saveError) {
      console.error("Erreur enregistrement association :", saveError);
      setError("Impossible d’enregistrer cette association.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggle = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`toggle-${id}`);
        setError("");

        await toggleAssociationActive(id);
        showNotice("Statut de publication mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement statut association :", toggleError);
        setError("Impossible de modifier le statut de cette association.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openPreview = useCallback(
    async (id) => {
      if (!id) return;

      const localItem = items.find((item) => item.id === id);

      if (localItem) {
        setPreviewItem(localItem);
        return;
      }

      try {
        setActionId(`preview-${id}`);

        const row = await getAssociationById(id);

        if (row) {
          setPreviewItem(row);
        }
      } catch (previewError) {
        console.error("Erreur aperçu association :", previewError);
        setError("Impossible d’afficher l’aperçu de cette association.");
      } finally {
        setActionId(null);
      }
    },
    [items],
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          nom_asso: "Cette association",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteAssociation(deleteItem.id);

      setDeleteItem(null);
      showNotice("Association supprimée.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression association :", deleteError);
      setError("Impossible de supprimer cette association.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  const openMediaPicker = useCallback(() => {
    setMediaPickerOpen(true);
  }, []);

  const closeMediaPicker = useCallback(() => {
    setMediaPickerOpen(false);
  }, []);

  const handleSelectMedia = useCallback(
    (media) => {
      if (!media) return;

      updateFormField("logo_asso", media.url || "");
      setMediaPickerOpen(false);
    },
    [updateFormField],
  );

  return {
    q,
    setQ,
    categorie,
    stateFilter,
    hasLogo,
    withGps,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,

    loading,
    saving,
    deleting,
    actionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    previewItem,
    deleteItem,
    mediaPickerOpen,

    fetchData,
    onSubmitSearch,
    changeCategorie,
    changeStateFilter,
    changeHasLogo,
    changeWithGps,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    openEditFromPreview,
    closeForm,
    updateFormField,
    onSave,

    onToggle,

    openPreview,
    closePreview,

    openDelete,
    closeDelete,
    onDelete,

    openMediaPicker,
    closeMediaPicker,
    handleSelectMedia,
  };
}